import type { PcfSettingsDto } from '../../../shared/api/pipeex/pcfSettingsClient';
import { getPipeexUiProfileMetadata } from '../../../shared/api/pipeex/pipeexProfileUiState';
import { parseJson } from '../../../shared/api/pipeex/pcfSettingsSerialization';
import type { BoltConfigurationInput } from '../../../shared/services/boltParameterSetup';
import type {
  AdditionalCategoriesConfiguration,
  ConnectorBindingConfiguration,
  MenuConfiguration,
} from '../../../shared/types';

function getSettingsDtoGroupingParameter(
  dto: PcfSettingsDto
): Pick<MenuConfiguration, 'groupingParameterName' | 'groupingParameterSource'> {
  const advancedRaw = parseJson<unknown>(dto.advancedSettings, {});
  const advanced = advancedRaw && typeof advancedRaw === 'object'
    ? advancedRaw as Record<string, unknown>
    : {};
  const baseRaw = typeof advanced.base === 'string'
    ? parseJson<unknown>(advanced.base, {})
    : advancedRaw;
  const base = baseRaw && typeof baseRaw === 'object'
    ? baseRaw as Record<string, unknown>
    : {};
  const groupingRaw = base.GroupingParameter;
  const grouping = groupingRaw && typeof groupingRaw === 'object'
    ? groupingRaw as Record<string, unknown>
    : {};

  return {
    groupingParameterName:
      typeof grouping.ParameterName === 'string' ? grouping.ParameterName : '',
    groupingParameterSource: grouping.Source === 'Type' ? 'Type' : 'Element',
  };
}

export function getSettingsDtoMenuConfiguration(
  dto: PcfSettingsDto
): MenuConfiguration | null {
  const menu = getPipeexUiProfileMetadata(dto.advancedSettings)?.wizard?.menu;
  if (!menu) {
    return null;
  }

  return {
    selectedOption: menu.selectedOption ?? null,
    objectTypeValue: menu.objectTypeValue ?? '',
    sKeyValue: menu.sKeyValue ?? '',
    pipingSpecValue: menu.pipingSpecValue ?? '',
    systemGroupingValue: menu.systemGroupingValue ?? '',
    ...getSettingsDtoGroupingParameter(dto),
  };
}

export function getSettingsDtoConnectorConfiguration(
  dto: PcfSettingsDto
): ConnectorBindingConfiguration | null {
  const connector = getPipeexUiProfileMetadata(dto.advancedSettings)?.wizard?.connector;
  if (!connector) {
    return null;
  }

  const field = (name: keyof typeof connector.fields) => ({
    value: connector.fields[name] ?? '',
  });
  return {
    parameterDestination: connector.parameterDestination,
    parameterBindingKind: connector.parameterBindingKind,
    endpoint1Value: field('endpoint1Value'),
    endpoint2Value: field('endpoint2Value'),
    endpoint3Value: field('endpoint3Value'),
    endpoint4Value: field('endpoint4Value'),
    materialValue: field('materialValue'),
    designStdValue: field('designStdValue'),
    pressureClassValue: field('pressureClassValue'),
    insulationSpecValue: field('insulationSpecValue'),
    insulationThicknessValue: field('insulationThicknessValue'),
    weightValue: field('weightValue'),
    tracingSpecValue: field('tracingSpecValue'),
    itemDescriptionValue: field('itemDescriptionValue'),
  };
}

/**
 * Bolt setup mode persisted inside the profile DTO (advancedSettings →
 * pipeexUi → wizard → bolt). The project-vs-family destination is a wizard
 * choice saved with the profile, so profile inspection must read it from the
 * DTO — profiles saved before the wizard snapshot existed (or by other
 * clients) carry no snapshot and keep the legacy project-level behavior.
 */
export function getSettingsDtoBoltConfigurationInput(
  dto: PcfSettingsDto
): BoltConfigurationInput | null {
  const wizard = getPipeexUiProfileMetadata(dto.advancedSettings)?.wizard;
  const bolt = wizard?.bolt;
  if (!bolt || !wizard.menu || !wizard.connector) {
    return null;
  }

  const { fields } = bolt;
  return {
    objectTypeParameterName: wizard.menu.objectTypeValue?.trim() ?? '',
    objectTypeBindingKind: wizard.connector.parameterBindingKind,
    parameterDestination: bolt.parameterDestination,
    parameterBindingKind: bolt.parameterBindingKind,
    projectParameterCategories: bolt.projectParameterCategories,
    boltModelingChoice: fields.boltModelingChoice ?? '',
    boltSetActiveValue: fields.boltSetActiveValue ?? '',
    boltItemDescriptionValue: fields.boltItemDescriptionValue ?? '',
    boltDiaValue: fields.boltDiaValue ?? '',
    boltQuantityValue: fields.boltQuantityValue ?? '',
    boltLengthValue: fields.boltLengthValue ?? '',
    boltMaterialValue: fields.boltMaterialValue ?? '',
    boltStandardValue: fields.boltStandardValue ?? '',
  };
}

/**
 * Additional-categories setup mode persisted in the same wizard snapshot.
 * Family-destination tag parameters (VALVE / SUPPORT / INSTRUMENT) live on
 * the matching family definitions, so profile inspection needs the persisted
 * destination to avoid scoring them against project-level catalogs.
 */
export function getSettingsDtoAdditionalCategoriesConfiguration(
  dto: PcfSettingsDto
): AdditionalCategoriesConfiguration | null {
  const additional = getPipeexUiProfileMetadata(dto.advancedSettings)?.wizard?.additionalCategories;
  if (!additional) {
    return null;
  }

  const { fields } = additional;
  return {
    parameterDestination: additional.parameterDestination,
    parameterBindingKind: additional.parameterBindingKind,
    projectParameterCategories: additional.projectParameterCategories,
    valveTagValue: { value: fields.valveTagValue ?? '' },
    supportsTagValue: { value: fields.supportsTagValue ?? '' },
    supportsSsrefValue: { value: fields.supportsSsrefValue ?? '' },
    instrumentTagValue: { value: fields.instrumentTagValue ?? '' },
  };
}

export function getSettingsDtoObjectTypeParameterName(
  dto: PcfSettingsDto
): string {
  return getPipeexUiProfileMetadata(dto.advancedSettings)
    ?.wizard?.menu.objectTypeValue?.trim() ?? '';
}
